/**
 * TEN-02/03: Panel de Super Administrador de Plataforma
 * Gestión de todos los tenants y planes.
 */
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, Building2, Search, Loader2, Settings, ArrowLeft, LogOut, Plus, BookOpen, Pencil, Globe, Check, Users, Download, ChevronLeft, ChevronRight, Mail, Calendar, Gift, Briefcase, Wrench, Hourglass } from 'lucide-react'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { useAuthStore } from '@/store/authStore'
import { useLogout } from '@/modules/auth/hooks/useAuth'
import { ProvisionTenantModal } from '@/modules/tenants/components/ProvisionTenantModal'
import {
  usePlatformTenants, useUpdateTenant, useToggleTenantStatus, useConvertTenant, useExtendTrial,
  useRetryProvisioning,
  useTenantLocalization, useUpdateTenantLocalization,
  useElectronicTaxpayers, useElectronicTaxpayerStats, useTriggerCEDownload,
  type PlatformTenant, type TenantLocalization, type ElectronicTaxpayer,
} from '../hooks/usePlatform'
import { formatRut } from '@/lib/utils'
import { useTenantLocale, formatDate, formatDateTime } from '@/lib/locale'
import { Button } from '@/components/Button'
import {
  useSIIAccounts, useCreateSIIAccount, useUpdateSIIAccount, useDeleteSIIAccount,
  type SIIAccount,
} from '@/modules/accounting/hooks/useAccounting'
import { EmptyState } from '@/components/EmptyState'

// ─── Badges ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { cls: string; dot: string }> = {
  active:       { cls: 'bg-success-surface text-success-content', dot: 'bg-emerald-400' },
  inactive:     { cls: 'bg-surface-muted text-content-muted',    dot: 'bg-content-subtle' },
  provisioning: { cls: 'bg-primary-50 text-primary-hover',       dot: 'bg-primary/60' },
  suspended:    { cls: 'bg-danger-surface text-danger-content',         dot: 'bg-danger-solid' },
  failed:       { cls: 'bg-danger-surface text-danger-content',         dot: 'bg-danger-solid' },
}

/** Un aprovisionamiento normal tarda ~1,5 min: antes de 15 se ofrecería lanzar
 *  uno segundo en paralelo con el que todavía corre. */
const MINUTOS_PARA_DAR_POR_COLGADO = 15

function puedeReintentar(t: { status: string; created_at: string }) {
  if (t.status === 'failed') return true
  if (t.status !== 'provisioning') return false
  return Date.now() - new Date(t.created_at).getTime() > MINUTOS_PARA_DAR_POR_COLGADO * 60_000
}

function StatusBadge({ status, display }: { status: string; display: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.inactive
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {display}
    </span>
  )
}

// ─── Account Type Badge ───────────────────────────────────────────────────────

const ACCOUNT_TYPE_CONFIG: Record<string, { cls: string }> = {
  production: { cls: 'bg-success-surface text-success-content' },
  trial:      { cls: 'bg-warning-surface text-warning-content' },
  demo:       { cls: 'bg-info-surface text-info-content' },
  // Azul y no verde: se distingue de producción a simple vista en el listado,
  // que es justamente lo que pide el contrato —«los tenants NFR se identifican
  // como tales»— aunque funcionalmente emitan igual.
  nfr:        { cls: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
}

function AccountTypeBadge({ type, display }: { type: string; display: string }) {
  const cfg = ACCOUNT_TYPE_CONFIG[type] ?? { cls: 'bg-surface-muted text-content-muted' }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      {display}
    </span>
  )
}

// ─── Edit Tenant Modal ─────────────────────────────────────────────────────────

function EditTenantModal({
  tenant,
  onClose,
}: {
  tenant: PlatformTenant
  onClose: () => void
}) {
  const [name, setName] = useState(tenant.name)
  const [licensedUsers, setLicensedUsers] = useState(tenant.licensed_users)
  const [maxUsers, setMaxUsers] = useState(tenant.max_users)
  const [posUsers, setPosUsers] = useState(tenant.licensed_pos_users ?? 0)
  const [allowHomologation, setAllowHomologation] = useState(tenant.allow_homologation)
  const [maxEmployees, setMaxEmployees] = useState<string>(
    tenant.max_employees != null ? String(tenant.max_employees) : ''
  )
  const { mutate: update, isPending } = useUpdateTenant(tenant.id)
  const [error, setError] = useState<string | null>(null)

  const handleSave = () => {
    setError(null)
    const maxEmployeesValue = maxEmployees.trim() === '' ? null : Math.max(0, parseInt(maxEmployees) || 0)
    update(
      { name, licensed_users: licensedUsers, max_users: maxUsers, allow_homologation: allowHomologation, max_employees: maxEmployeesValue, licensed_pos_users: posUsers },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setError(err.response?.data?.error?.message ?? 'Error al guardar')
        },
      }
    )
  }

  return (
    <Modal open onClose={onClose} title="Editar Tenant" size="md"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted hover:bg-soft rounded-lg transition-colors">
          Cancelar
        </button>
        <Button onClick={handleSave} disabled={!name.trim()} isPending={isPending}>
          Guardar
        </Button>
      </>}
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-danger-surface border border-danger-border text-danger-content text-sm">⚠️ {error}</div>
        )}

        <div>
          <label htmlFor="platformadminpage-nombre-2" className="block text-sm font-medium text-content mb-1.5">Nombre</label>
          <input id="platformadminpage-nombre-2"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div>
          <label htmlFor="platformadminpage-identificador" className="block text-sm font-medium text-content mb-1.5">Identificador</label>
          <input id="platformadminpage-identificador"
            value={tenant.schema_name}
            disabled
            className="w-full px-3 py-2.5 rounded-lg border border-divider bg-surface-muted text-sm text-content-subtle"
          />
        </div>

        {tenant.account_type === 'production' && (
          <div>
            <label htmlFor="platformadminpage-usuarios-contratados-2" className="block text-sm font-medium text-content mb-1.5">Usuarios contratados</label>
            <input id="platformadminpage-usuarios-contratados-2"
              type="number"
              min={1}
              value={licensedUsers}
              onChange={e => setLicensedUsers(parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-content-subtle mt-1">Costo: ${licensedUsers * 15} USD/mes</p>
          </div>
        )}

        {tenant.account_type !== 'production' && (
          <div>
            <label htmlFor="platformadminpage-maximo-de-usuarios" className="block text-sm font-medium text-content mb-1.5">Máximo de usuarios</label>
            <input id="platformadminpage-maximo-de-usuarios"
              type="number"
              min={1}
              value={maxUsers}
              onChange={e => setMaxUsers(parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        )}

        {tenant.account_type !== 'production' && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowHomologation}
              onChange={e => setAllowHomologation(e.target.checked)}
              className="w-4 h-4 rounded border-divider-strong text-primary focus:ring-primary"
            />
            <span className="text-sm text-content">Permitir SII Homologación</span>
          </label>
        )}

        <div>
          <label htmlFor="platformadminpage-licencias-pos" className="block text-sm font-medium text-content mb-1.5">Licencias POS</label>
          <input id="platformadminpage-licencias-pos"
            type="number"
            min={0}
            value={posUsers}
            onChange={e => setPosUsers(Math.max(0, parseInt(e.target.value) || 0))}
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-content-subtle mt-1">
            Cajeros que solo operan el terminal del POS y no entran a la web. En uso: {tenant.active_pos_users ?? 0}. Precio por definir: todavía no entra al costo.
          </p>
        </div>

        <div>
          <label htmlFor="platformadminpage-maximo-de-trabajadores" className="block text-sm font-medium text-content mb-1.5">Máximo de trabajadores</label>
          <input id="platformadminpage-maximo-de-trabajadores"
            type="number"
            min={0}
            value={maxEmployees}
            onChange={e => setMaxEmployees(e.target.value)}
            placeholder="Automático según plan"
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-content-subtle mt-1">
            {maxEmployees.trim() === ''
              ? `Sin override: el plan permite ${tenant.employee_limit} trabajadores activos.`
              : 'Override del tope del módulo Nómina. Vacíalo para volver al cálculo automático por plan.'}
          </p>
        </div>
      </div>

    </Modal>
  )
}

// ─── Convert to Production Modal ─────────────────────────────────────────────

function ConvertTenantModal({
  tenant,
  onClose,
}: {
  tenant: PlatformTenant
  onClose: () => void
}) {
  const [licensedUsers, setLicensedUsers] = useState(1)
  const { mutate: convert, isPending } = useConvertTenant()
  const [error, setError] = useState<string | null>(null)

  const handleConvert = () => {
    setError(null)
    convert(
      { id: tenant.id, licensed_users: licensedUsers },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setError(err.response?.data?.error?.message ?? 'Error al convertir')
        },
      }
    )
  }

  return (
    <Modal open onClose={onClose} title="Convertir a Producción" size="md"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted hover:bg-soft rounded-lg transition-colors">
          Cancelar
        </button>
        <Button variant="success" onClick={handleConvert} disabled={licensedUsers < 1} isPending={isPending}>
          Convertir a Producción
        </Button>
      </>}
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-danger-surface border border-danger-border text-danger-content text-sm">⚠️ {error}</div>
        )}
        <p className="text-sm text-content-muted">
          Convierte <strong>{tenant.name}</strong> de {tenant.account_type_display} a Producción.
        </p>
        <div>
          <label htmlFor="platformadminpage-usuarios-contratados" className="block text-sm font-medium text-content mb-1.5">Usuarios contratados</label>
          <input id="platformadminpage-usuarios-contratados"
            type="number"
            min={1}
            value={licensedUsers}
            onChange={e => setLicensedUsers(parseInt(e.target.value) || 1)}
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-content-subtle mt-1">Costo mensual: ${licensedUsers * 15} USD/mes</p>
        </div>
      </div>

    </Modal>
  )
}

// ─── Extend Trial Modal ───────────────────────────────────────────────────────

function ExtendTrialModal({
  tenant,
  onClose,
}: {
  tenant: PlatformTenant
  onClose: () => void
}) {
  const esDemo = tenant.account_type === 'demo'
  const [days, setDays] = useState(esDemo ? 7 : 30)
  const { mutate: extend, isPending } = useExtendTrial()
  const [error, setError] = useState<string | null>(null)

  const handleExtend = () => {
    setError(null)
    extend(
      { id: tenant.id, days },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setError(err.response?.data?.error?.message ?? 'Error al extender')
        },
      }
    )
  }

  return (
    <Modal open onClose={onClose} title={<>Extender {esDemo ? 'demo' : 'trial'}</>} size="md"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted hover:bg-soft rounded-lg transition-colors">
          Cancelar
        </button>
        <Button variant="warning" onClick={handleExtend} disabled={days < 1} isPending={isPending}>
          Extender
        </Button>
      </>}
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-danger-surface border border-danger-border text-danger-content text-sm">⚠️ {error}</div>
        )}
        <p className="text-sm text-content-muted">
          Extiende la vigencia de <strong>{tenant.name}</strong>
          {tenant.days_until_expiry !== null && (
            <> — hoy {tenant.days_until_expiry === 0 ? 'vence hoy' : `le quedan ${tenant.days_until_expiry} día${tenant.days_until_expiry !== 1 ? 's' : ''}`}</>
          )}.
        </p>
        {tenant.status === 'suspended' && (
          <p className="text-xs text-content-subtle">
            La cuenta está suspendida por vencimiento: al extenderla vuelve a quedar activa.
          </p>
        )}
        <div>
          <label htmlFor="platformadminpage-dias-a-agregar" className="block text-sm font-medium text-content mb-1.5">Días a agregar</label>
          <input id="platformadminpage-dias-a-agregar"
            type="number"
            min={1}
            value={days}
            onChange={e => setDays(parseInt(e.target.value) || 1)}
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

    </Modal>
  )
}

// ─── Tenants Tab ─────────────────────────────────────────────────────────────

function TenantsTab() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [editTenant, setEditTenant] = useState<PlatformTenant | null>(null)
  const [convertTenant, setConvertTenant] = useState<PlatformTenant | null>(null)
  const [extendTenant, setExtendTenant] = useState<PlatformTenant | null>(null)
  const [showProvision, setShowProvision] = useState(false)
  const locale = useTenantLocale()

  const { data, isLoading } = usePlatformTenants({ status: statusFilter || undefined })
  const { mutate: toggleStatus, isPending: isToggling } = useToggleTenantStatus()
  const { mutate: retryProvisioning, isPending: isRetrying } = useRetryProvisioning()

  const tenants = (data?.tenants ?? []).filter(t =>
    !search ||
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.schema_name.toLowerCase().includes(search.toLowerCase()) ||
    t.tax_id.includes(search.replace(/\./g, ''))
  )

  return (
    <div>
      {editTenant && (
        <EditTenantModal tenant={editTenant} onClose={() => setEditTenant(null)} />
      )}
      {convertTenant && (
        <ConvertTenantModal tenant={convertTenant} onClose={() => setConvertTenant(null)} />
      )}
      {extendTenant && (
        <ExtendTrialModal tenant={extendTenant} onClose={() => setExtendTenant(null)} />
      )}
      {showProvision && (
        <ProvisionTenantModal onClose={() => setShowProvision(false)} />
      )}

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar por nombre o RUT"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre o RUT..."
            className="w-full pl-8 pr-4 py-2 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <select aria-label="Todos los estados"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
          <option value="provisioning">Aprovisionando</option>
          <option value="failed">Falló el aprovisionamiento</option>
        </select>
        <Button
          variant="info"
          onClick={() => setShowProvision(true)}
          leftIcon={<Plus size={15} />}
          className="whitespace-nowrap"
        >
          Crear Tenant
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-content-subtle">
          <Loader2 size={24} className="animate-spin mx-auto mb-2" />
          Cargando tenants...
        </div>
      ) : (
        <DataTable
          columns={[
            {
              key: 'name', header: 'Empresa',
              render: t => (
                <>
                  <p className="font-medium text-content">{t.name}</p>
                  <p className="text-xs text-content-subtle">{t.schema_name}</p>
                  {t.tax_id && <p className="text-xs text-content-subtle">{formatRut(t.tax_id)}</p>}
                </>
              ),
            },
            {
              key: 'country', header: 'País',
              render: t => (
                <span className="text-xs font-medium text-content-muted bg-surface-muted px-2 py-0.5 rounded">{t.country}</span>
              ),
            },
            {
              key: 'account_type', header: 'Tipo de cuenta',
              render: t => (
                <>
                  <AccountTypeBadge type={t.account_type} display={t.account_type_display} />
                  {t.monthly_cost_usd !== null && (
                    <p className="text-xs text-content-subtle mt-0.5">${t.monthly_cost_usd}/mes</p>
                  )}
                  {/* La caducidad se muestra para cualquier cuenta temporal, no
                      solo las trial: las demo también se suspenden solas. */}
                  {t.trial_ends_at && t.account_type !== 'production' && (
                    <p className={`text-xs mt-0.5 ${(t.days_until_expiry ?? 99) <= 3
                        ? 'text-danger-content-soft font-medium'
                        : 'text-warning-content-soft'}`}>
                      Vence: {formatDate(t.trial_ends_at, locale)}
                      {t.days_until_expiry !== null && (
                        <> · {t.days_until_expiry === 0
                          ? 'hoy'
                          : `${t.days_until_expiry} día${t.days_until_expiry !== 1 ? 's' : ''}`}</>
                      )}
                    </p>
                  )}
                </>
              ),
            },
            {
              key: 'usuarios', header: 'Usuarios',
              render: t => (
                <span className="text-xs">
                  {t.active_users} / {t.account_type === 'production' ? t.licensed_users : t.max_users}
                </span>
              ),
            },
            {
              key: 'status', header: 'Estado',
              render: t => (
                <div className="flex flex-col gap-0.5">
                  <StatusBadge status={t.status} display={t.status_display} />
                  {t.status === 'failed' && t.provisioning_error && (
                    <span className="text-[11px] text-danger-content max-w-[220px] truncate" title={t.provisioning_error}>
                      {t.provisioning_error}
                    </span>
                  )}
                </div>
              ),
            },
            { key: 'created_at', header: 'Creado', render: t => <span className="text-xs">{formatDate(t.created_at, locale)}</span> },
            {
              key: 'acciones', header: '',
              render: t => (
                <div className="flex items-center gap-1 justify-end flex-wrap">
                  <button
                    onClick={() => setEditTenant(t)}
                    className="px-2 py-1 text-xs text-primary hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    Editar
                  </button>
                  {t.account_type !== 'production' && (
                    <button
                      onClick={() => setConvertTenant(t)}
                      className="px-2 py-1 text-xs text-success-content-soft hover:bg-success-surface rounded-lg transition-colors"
                    >
                      → Prod
                    </button>
                  )}
                  {/* Extender aplica a toda cuenta temporal. Con solo `trial`,
                      una demo suspendida por vencimiento no tenía cómo
                      reactivarse por UI. */}
                  {t.account_type !== 'production' && (
                    <Button variant="ghost-warning" size="sm" onClick={() => setExtendTenant(t)}>
                      Extender
                    </Button>
                  )}
                  {t.status === 'active' ? (
                    <button
                      onClick={() => toggleStatus({ id: t.id, action: 'deactivate' })}
                      disabled={isToggling}
                      className="px-2 py-1 text-xs text-danger-content-soft hover:bg-danger-surface rounded-lg transition-colors"
                    >
                      Suspender
                    </button>
                  ) : t.status === 'inactive' ? (
                    <button
                      onClick={() => toggleStatus({ id: t.id, action: 'activate' })}
                      disabled={isToggling}
                      className="px-2 py-1 text-xs text-success-content-soft hover:bg-success-surface rounded-lg transition-colors"
                    >
                      Activar
                    </button>
                  ) : null}
                  {puedeReintentar(t) && (
                    <Button variant="ghost-warning" size="sm" disabled={isRetrying}
                      onClick={() => retryProvisioning(t.id)}
                      title="Vuelve a lanzar el aprovisionamiento. Es seguro repetirlo.">
                      Reintentar
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
          rows={tenants}
          rowKey={t => t.id}
          // El aviso estaba DESPUÉS de la tabla, así que con cero tenants se
          // dibujaban los encabezados y el mensaje colgando abajo.
          empty={<EmptyState title="No se encontraron tenants" size="sm" />}
          minWidth={1000}
          caption="Tenants de la plataforma"
        />
      )}

      <p className="mt-3 text-xs text-content-subtle">{data?.count ?? 0} tenant(s) total</p>
    </div>
  )
}


// ─── SII Accounts Tab ────────────────────────────────────────────────────────

function SIIAccountEditModal({
  account,
  onClose,
}: {
  account?: SIIAccount | null
  onClose: () => void
}) {
  const isEdit = !!account
  const [code, setCode] = useState(account?.code ?? '')
  const [name, setName] = useState(account?.name ?? '')
  const [error, setError] = useState<string | null>(null)
  const create = useCreateSIIAccount()
  const update = useUpdateSIIAccount(account?.id ?? 0)
  const isPending = create.isPending || update.isPending

  const handleSave = () => {
    if (!code || !name) return
    setError(null)
    const payload = { code, name }
    const cb = {
      onSuccess: () => onClose(),
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string }; code?: string[] } } }
        setError(err.response?.data?.error?.message || err.response?.data?.code?.[0] || 'Error al guardar')
      },
    }
    if (isEdit) update.mutate(payload, cb)
    else create.mutate(payload, cb)
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar Cuenta SII' : 'Nueva Cuenta SII'} size="md"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted hover:bg-soft rounded-lg">Cancelar</button>
        <Button variant="info" onClick={handleSave} disabled={!code || !name} isPending={isPending}>
          {isEdit ? 'Guardar' : 'Crear'}
        </Button>
      </>}
    >
      <div className="space-y-4">
        {error && <div className="p-3 rounded-lg bg-danger-surface border border-danger-border text-danger-content text-sm">{error}</div>}
        <div>
          <label htmlFor="platformadminpage-codigo" className="block text-sm font-medium text-content mb-1.5">Código *</label>
          <input id="platformadminpage-codigo"
            value={code} onChange={e => setCode(e.target.value)} placeholder="ej. 1220.010"
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label htmlFor="platformadminpage-nombre" className="block text-sm font-medium text-content mb-1.5">Nombre *</label>
          <input id="platformadminpage-nombre"
            value={name} onChange={e => setName(e.target.value)} placeholder="ej. Disponible"
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
    </Modal>
  )
}

function SIIAccountsTab() {
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<SIIAccount | null>(null)
  const [creating, setCreating] = useState(false)
  const { data, isLoading } = useSIIAccounts({ search: search || undefined })
  const deleteMutation = useDeleteSIIAccount()
  const accounts = data?.accounts ?? []

  return (
    <div>
      {(creating || editing) && (
        <SIIAccountEditModal account={editing} onClose={() => { setEditing(null); setCreating(false) }} />
      )}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar por código o nombre"
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por código o nombre..."
            className="w-full pl-8 pr-4 py-2 rounded-lg border border-divider-strong text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <Button
          variant="info"
          onClick={() => { setEditing(null); setCreating(true) }}
          leftIcon={<Plus size={15} />}
          className="whitespace-nowrap"
        >
          Nueva Cuenta
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-content-subtle">
          <Loader2 size={24} className="animate-spin mx-auto mb-2" />Cargando...
        </div>
      ) : (
        <DataTable
          columns={[
            { key: 'code', header: 'Código', render: a => <span className="font-mono text-content">{a.code}</span> },
            { key: 'name', header: 'Nombre' },
            {
              key: 'is_active', header: 'Estado',
              render: a => (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  a.is_active ? 'bg-success-surface text-success-content' : 'bg-surface-muted text-content-muted'
                }`}>
                  {a.is_active ? 'Activa' : 'Inactiva'}
                </span>
              ),
            },
            {
              key: 'acciones', header: '',
              render: a => (
                <div className="text-right">
                  <button onClick={() => setEditing(a)} className="px-2 py-1 text-xs text-primary hover:bg-primary-50 rounded-lg">
                    <Pencil size={12} className="inline mr-1" />Editar
                  </button>
                </div>
              ),
            },
          ]}
          rows={accounts}
          rowKey={a => a.id}
          empty={<EmptyState title="Sin cuentas SII cargadas" size="sm" />}
          minWidth={520}
          caption="Cuentas del SII de la plataforma"
        />
      )}
      <p className="mt-3 text-xs text-content-subtle">{data?.count ?? 0} cuenta(s) SII</p>
    </div>
  )
}

// ─── Localization Config Modal ────────────────────────────────────────────────

const COUNTRY_OPTIONS = [
  { value: 'CL', label: 'Chile' },
  { value: 'PE', label: 'Peru' },
  { value: 'AR', label: 'Argentina' },
  { value: 'MX', label: 'Mexico' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'CO', label: 'Colombia' },
]

const CURRENCY_OPTIONS = [
  { value: 'CLP', label: 'CLP — Peso Chileno' },
  { value: 'PEN', label: 'PEN — Sol Peruano' },
  { value: 'ARS', label: 'ARS — Peso Argentino' },
  { value: 'MXN', label: 'MXN — Peso Mexicano' },
  { value: 'USD', label: 'USD — Dolar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'COP', label: 'COP — Peso Colombiano' },
  { value: 'UF', label: 'UF — Unidad de Fomento' },
]

const COUNTRY_DEFAULTS: Record<string, { currency: string; tax_name: string; tax_rate: string }> = {
  CL: { currency: 'CLP', tax_name: 'IVA', tax_rate: '19' },
  PE: { currency: 'PEN', tax_name: 'IGV', tax_rate: '18' },
  AR: { currency: 'ARS', tax_name: 'IVA', tax_rate: '21' },
  MX: { currency: 'MXN', tax_name: 'IVA', tax_rate: '16' },
  EC: { currency: 'USD', tax_name: 'IVA', tax_rate: '15' },
  CO: { currency: 'COP', tax_name: 'IVA', tax_rate: '19' },
}

function LocalizationEditModal({
  tenant,
  localization,
  onClose,
}: {
  tenant: PlatformTenant
  localization: TenantLocalization
  onClose: () => void
}) {
  const isNew = !localization.configured
  const defaults = COUNTRY_DEFAULTS[tenant.country] ?? COUNTRY_DEFAULTS.CL

  const [countryCode, setCountryCode] = useState(localization.country_code ?? tenant.country ?? 'CL')
  const [currency, setCurrency] = useState(localization.currency ?? defaults.currency)
  const [taxName, setTaxName] = useState(localization.tax_name ?? defaults.tax_name)
  const [taxRate, setTaxRate] = useState(localization.tax_rate ?? defaults.tax_rate)
  const [isConfigured, setIsConfigured] = useState(localization.is_configured ?? false)
  const [error, setError] = useState<string | null>(null)

  const { mutate: update, isPending } = useUpdateTenantLocalization(tenant.id)

  const handleCountryChange = (code: string) => {
    setCountryCode(code)
    if (isNew) {
      const d = COUNTRY_DEFAULTS[code]
      if (d) {
        setCurrency(d.currency)
        setTaxName(d.tax_name)
        setTaxRate(d.tax_rate)
      }
    }
  }

  const handleSave = () => {
    setError(null)
    update(
      {
        country_code: countryCode,
        currency,
        tax_name: taxName,
        tax_rate: taxRate,
        is_configured: isConfigured,
      },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setError(err.response?.data?.error?.message ?? 'Error al guardar')
        },
      }
    )
  }

  return (
    <Modal open onClose={onClose} title={<>{isNew ? 'Configurar Localización' : 'Editar Localización'} — {tenant.name}</>} size="md"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted hover:bg-soft rounded-lg transition-colors">
          Cancelar
        </button>
        <Button
          variant="info"
          onClick={handleSave}
          disabled={!countryCode || !currency}
          isPending={isPending}
        >
          {isNew ? 'Configurar' : 'Guardar'}
        </Button>
      </>}
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-danger-surface border border-danger-border text-danger-content text-sm">{error}</div>
        )}

        <div>
          <label htmlFor="platformadminpage-pais" className="block text-sm font-medium text-content mb-1.5">País</label>
          <select id="platformadminpage-pais"
            value={countryCode}
            onChange={e => handleCountryChange(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            {COUNTRY_OPTIONS.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="platformadminpage-moneda" className="block text-sm font-medium text-content mb-1.5">Moneda</label>
          <select id="platformadminpage-moneda"
            value={currency}
            onChange={e => setCurrency(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            {CURRENCY_OPTIONS.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="platformadminpage-nombre-impuesto" className="block text-sm font-medium text-content mb-1.5">Nombre Impuesto</label>
            <input id="platformadminpage-nombre-impuesto"
              value={taxName}
              onChange={e => setTaxName(e.target.value)}
              placeholder="ej. IVA"
              className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div>
            <label htmlFor="platformadminpage-tasa" className="block text-sm font-medium text-content mb-1.5">Tasa (%)</label>
            <input id="platformadminpage-tasa"
              type="number"
              value={taxRate}
              onChange={e => setTaxRate(e.target.value)}
              placeholder="ej. 19"
              className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={isConfigured}
            onChange={e => setIsConfigured(e.target.checked)}
            className="w-4 h-4 rounded border-divider-strong text-info-content-soft focus:ring-violet-500"
          />
          <span className="text-sm text-content">Marcar como configurado</span>
        </label>
      </div>

    </Modal>
  )
}

// ─── Localization Tab ─────────────────────────────────────────────────────────

function LocalizationTab() {
  const [search, setSearch] = useState('')
  const [selectedTenant, setSelectedTenant] = useState<PlatformTenant | null>(null)

  const { data, isLoading } = usePlatformTenants()
  const { data: locData, isLoading: isLocLoading } = useTenantLocalization(selectedTenant?.id ?? null)

  const tenants = (data?.tenants ?? []).filter(t =>
    !search ||
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.schema_name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      {selectedTenant && locData && (
        <LocalizationEditModal
          tenant={selectedTenant}
          localization={locData}
          onClose={() => setSelectedTenant(null)}
        />
      )}

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar tenant"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar tenant..."
            className="w-full pl-8 pr-4 py-2 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <DataTable
        columns={columnasDeTenant({
          onSelect: t => setSelectedTenant(t),
          tenantEnCarga: isLocLoading ? (selectedTenant?.id ?? null) : null,
        })}
        rows={tenants}
        rowKey={t => t.id}
        loading={isLoading}
        loadingNode={
          <div className="text-center py-12 text-content-subtle">
            <Loader2 size={24} className="animate-spin mx-auto mb-2" />
            Cargando tenants...
          </div>
        }
        empty={<EmptyState title="No se encontraron tenants" size="sm" />}
        minWidth={760}
        surface={false}
      />

      <p className="mt-3 text-xs text-content-subtle">{data?.count ?? 0} tenant(s) total</p>
    </div>
  )
}

/**
 * El estado de localización de UN tenant.
 *
 * 🔴 **Esta celda es un componente y no un `render` suelto, y ahí está todo el
 * asunto.** `TenantLocalizationRow` —la fila que esto reemplaza— consultaba
 * `useTenantLocalization(tenant.id)` adentro, o sea el «componente-fila» que el
 * inventario daba por desarmable **sí tenía un hook propio**. Una función que
 * arma columnas no puede llamarlo: los hooks solo viven en componentes.
 *
 * La salida es mover el hook una capa más adentro —a la celda— en vez de subirlo
 * a la página: subirlo obligaría a resolver una consulta por tenant antes de
 * pintar la tabla, y la lista dejaría de aparecer hasta que respondan todas.
 * Cada celda sigue llegando cuando llega, como antes.
 */
function CeldaLocalizacion({ tenant }: { tenant: PlatformTenant }) {
  const { data: loc } = useTenantLocalization(tenant.id)
  if (!loc) return <span className="text-xs text-content-subtle">...</span>
  return loc.configured ? (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-success-surface text-success-content">
      <Check size={12} />
      {loc.country_code} / {loc.currency} / {loc.tax_name} {loc.tax_rate}%
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-warning-surface text-warning-content">
      <Globe size={12} />
      Sin configurar
    </span>
  )
}

/**
 * El botón de la última columna.
 *
 * ⚠️ Repite `useTenantLocalization` porque su etiqueta depende de lo mismo
 * («Editar» si ya está configurado, «Configurar» si no). **No agrega una
 * petición**: React Query comparte la entrada de caché por clave, así que las
 * dos celdas de la misma fila leen la misma respuesta.
 */
function BotonLocalizacion({ tenant, onSelect, isLoadingModal }: {
  tenant: PlatformTenant
  onSelect: () => void
  isLoadingModal: boolean
}) {
  const { data: loc } = useTenantLocalization(tenant.id)
  return (
    <div className="text-right">
      <Button
        variant="ghost-info"
        size="sm"
        onClick={onSelect}
        disabled={isLoadingModal}
        isPending={isLoadingModal}
        leftIcon={loc?.configured ? <Pencil size={12} /> : <Globe size={12} />}
        className="ml-auto"
      >
        {loc?.configured ? 'Editar' : 'Configurar'}
      </Button>
    </div>
  )
}

/**
 * Las columnas del listado de tenants con su localización.
 *
 * ⚠️ **Sin `sortable`**: el listado lo pagina el servidor, y ordenar acá
 * ordenaría solo la página que se ve.
 */
function columnasDeTenant({ onSelect, tenantEnCarga }: {
  onSelect: (t: PlatformTenant) => void
  tenantEnCarga: PlatformTenant['id'] | null
}): DataTableColumn<PlatformTenant>[] {
  return [
    { key: 'name', header: 'Empresa',
      render: t => (
        <>
          <p className="font-medium text-content">{t.name}</p>
          <p className="text-xs text-content-subtle">{t.schema_name}</p>
        </>
      ) },
    { key: 'country', header: 'País',
      render: t => (
        <span className="text-xs font-medium text-content-muted bg-surface-muted px-2 py-0.5 rounded">
          {t.country}
        </span>
      ) },
    { key: 'status', header: 'Estado',
      render: t => <StatusBadge status={t.status} display={t.status_display} /> },
    { key: 'localization', header: 'Localización',
      render: t => <CeldaLocalizacion tenant={t} /> },
    { key: 'acciones', header: <span className="sr-only">Acciones</span>,
      render: t => (
        <BotonLocalizacion
          tenant={t}
          onSelect={() => onSelect(t)}
          isLoadingModal={tenantEnCarga === t.id}
        />
      ) },
  ]
}

// ─── Contribuyentes Electrónicos Tab ──────────────────────────────────────────

function ElectronicTaxpayersTab() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [environment, setEnvironment] = useState('produccion')
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 50
  const locale = useTenantLocale()

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current) }, [])
  const handleSearch = (val: string) => {
    setSearch(val)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setDebouncedSearch(prev => {
        if (prev === val) return prev
        setPage(1)
        return val
      })
    }, 400)
  }

  const { data, isLoading } = useElectronicTaxpayers({
    search: debouncedSearch || undefined,
    environment: environment || undefined,
    page,
    page_size: PAGE_SIZE,
  })
  const { data: stats } = useElectronicTaxpayerStats()
  const downloadMutation = useTriggerCEDownload()

  const taxpayers = data?.taxpayers ?? []
  const total = data?.count ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)
  const lastUpdated = data?.last_updated || stats?.last_updated

  return (
    <div>
      {/* Stats bar */}
      <div className="flex items-center gap-4 mb-4 text-xs text-content-muted">
        <span className="font-medium text-content">
          {(stats?.total ?? 0).toLocaleString('es-CL')} contribuyentes registrados
        </span>
        {stats?.by_environment && Object.entries(stats.by_environment).map(([env, count]) => (
          <span key={env} className="px-2 py-0.5 bg-surface-muted rounded-full">
            {env === 'produccion' ? 'Prod' : 'Homol'}: {(count as number).toLocaleString('es-CL')}
          </span>
        ))}
        {lastUpdated && (
          <span>Actualizado: {formatDateTime(lastUpdated, locale)}</span>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar por RUT (ej. 76354771-K o 76354771)"
            value={search} onChange={e => handleSearch(e.target.value)}
            placeholder="Buscar por RUT (ej. 76354771-K o 76354771)"
            className="w-full pl-8 pr-4 py-2 rounded-lg border border-divider-strong text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <select aria-label="Ambiente SII"
          value={environment} onChange={e => { setEnvironment(e.target.value); setPage(1) }}
          className="px-3 py-2 rounded-lg border border-divider-strong text-sm bg-surface"
        >
          <option value="produccion">Producción</option>
          <option value="homologacion">Homologación</option>
        </select>
        <Button
          variant="info"
          onClick={() => downloadMutation.mutate()}
          isPending={downloadMutation.isPending}
          leftIcon={<Download size={14} />}
          className="whitespace-nowrap"
        >
          Actualizar
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-content-subtle">
          <Loader2 size={24} className="animate-spin mx-auto mb-2" />Cargando...
        </div>
      ) : (
        <DataTable
          columns={[
            { key: 'tax_id', header: 'RUT', render: t => <span className="font-mono text-content whitespace-nowrap">{t.tax_id}</span> },
            {
              key: 'legal_name', header: 'Razón Social',
              render: t => <span className="block max-w-xs truncate" title={t.legal_name}>{t.legal_name}</span>,
            },
            {
              key: 'email_intercambio', header: 'Email Intercambio',
              render: t => t.email_intercambio ? (
                <span className="flex items-center gap-1">
                  <Mail size={12} className="text-content-subtle shrink-0" />
                  <span className="truncate max-w-[200px]" title={t.email_intercambio}>{t.email_intercambio}</span>
                </span>
              ) : (
                <span className="text-content-subtle">—</span>
              ),
            },
            {
              key: 'environment', header: 'Ambiente',
              render: t => (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  t.environment === 'produccion' ? 'bg-success-surface text-success-content' : 'bg-warning-surface text-warning-content'
                }`}>
                  {t.environment_display}
                </span>
              ),
            },
            {
              key: 'resolucion', header: 'Resolución',
              render: t => <span className="text-xs whitespace-nowrap">{t.resolution_number} — {t.resolution_date}</span>,
            },
          ]}
          rows={taxpayers}
          rowKey={t => t.id}
          empty={
            <div className="text-center py-10 text-sm text-content-subtle">
              {/* El padrón se busca SOLO por RUT: indexar razón social y correo
                  costaba 227 MB para una búsqueda que no se usaba. Sin este
                  mensaje, escribir un nombre parece un error del sistema. */}
              {data?.search_ignored ? (
                <>
                  <p className="text-content-muted">La búsqueda del padrón es por <strong>RUT</strong>.</p>
                  <p className="mt-1">Escribe el RUT completo (76354771-K) o solo su número (76354771).</p>
                </>
              ) : debouncedSearch ? (
                <p>Sin resultados para ese RUT en {environment === 'produccion' ? 'Producción' : 'Homologación'}</p>
              ) : (
                <p>Sin contribuyentes cargados</p>
              )}
            </div>
          }
          minWidth={900}
          caption="Padrón de contribuyentes electrónicos"
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-content-muted">
          <span>{total.toLocaleString('es-CL')} resultado(s)</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-soft disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs">Pág. {page} de {totalPages.toLocaleString('es-CL')}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1.5 rounded-lg hover:bg-soft disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

import { SeasonalThemesTab } from './SeasonalThemesTab'
import { MaintenanceWindowsTab } from './MaintenanceWindowsTab'
import { SiiStatusTab } from './SiiStatusTab'
import { PlatformUpdatesTab } from './PlatformUpdatesTab'
import { InboundEmailsTab } from './InboundEmailsTab'
import { FirmsAdminTab } from '@/modules/firm/components/FirmsAdminTab'
import { Modal } from '@/components/ds/Modal'

const TABS = [
  { key: 'tenants', label: 'Tenants', icon: Building2 },
  { key: 'firms', label: 'Estudios Contables', icon: Briefcase },
  { key: 'inbound-emails', label: 'Correos entrantes', icon: Mail },
  { key: 'sii', label: 'Plan Cuentas SII', icon: BookOpen },
  { key: 'ce', label: 'Contribuyentes', icon: Users },
  { key: 'seasonal', label: 'Temas Festivos', icon: Calendar },
  { key: 'maintenance', label: 'Mantenimiento', icon: Wrench },
  { key: 'sii-status', label: 'Estado del SII', icon: Hourglass },
  { key: 'updates', label: 'Novedades', icon: Gift },
  { key: 'config', label: 'Configuración', icon: Settings },
]

export function PlatformAdminPage() {
  const [activeTab, setActiveTab] = useState('tenants')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { mutate: logout } = useLogout()

  return (
    <div className="min-h-screen bg-surface-muted">
      {/* Topbar */}
      <header className="bg-violet-900 text-white px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1.5 text-violet-200 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft size={15} />
            Dashboard
          </button>
          <div className="w-px h-5 bg-violet-700" />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-violet-700 flex items-center justify-center">
              <Shield size={13} className="text-violet-200" />
            </div>
            <span className="text-sm font-medium">Panel de Plataforma</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-violet-300 hidden sm:block">{user?.email}</span>
          <button
            onClick={() => logout()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-violet-200 hover:text-white hover:bg-violet-800 rounded-lg transition-colors"
          >
            <LogOut size={13} />
            Salir
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-lg bg-info-surface-strong flex items-center justify-center">
            <Shield size={18} className="text-info-content-soft" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-content">Panel de Plataforma</h1>
            <p className="text-sm text-content-muted mt-0.5">Administración global de tenants y planes</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
          <div className="flex border-b border-divider">
            {TABS.map(tab => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-colors border-b-2
                    ${activeTab === tab.key
                      ? 'border-violet-500 text-info-content bg-violet-50/50 dark:bg-violet-900/20'
                      : 'border-transparent text-content-muted hover:text-content hover:bg-soft'
                    }`}
                >
                  <Icon size={15} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          <div className="p-6">
            {activeTab === 'tenants' && <TenantsTab />}
            {activeTab === 'firms' && <FirmsAdminTab />}
            {activeTab === 'inbound-emails' && <InboundEmailsTab />}
            {activeTab === 'sii' && <SIIAccountsTab />}
            {activeTab === 'ce' && <ElectronicTaxpayersTab />}
            {activeTab === 'seasonal' && <SeasonalThemesTab />}
            {activeTab === 'maintenance' && <MaintenanceWindowsTab />}
            {activeTab === 'sii-status' && <SiiStatusTab />}
            {activeTab === 'updates' && <PlatformUpdatesTab />}
            {activeTab === 'config' && <LocalizationTab />}
          </div>
        </div>
      </div>
    </div>
  )
}
